import { PixelLoveGame } from './pixel/game/PixelLoveGame';
import { loadAssets } from './pixel/render/AssetLoader';

class App {
  private readonly game: PixelLoveGame;
  private animationFrame = 0;
  private readonly tickBound: (time: number) => void;
  private readonly resizeBound: () => void;

  constructor(root: HTMLElement) {
    this.game = new PixelLoveGame(root);
    this.tickBound = this.tick.bind(this);
    this.resizeBound = this.onResize.bind(this);
    window.addEventListener('resize', this.resizeBound);
    this.onResize();
    this.animationFrame = requestAnimationFrame(this.tickBound);
  }

  private tick(time: number): void {
    this.game.update(time);
    this.animationFrame = requestAnimationFrame(this.tickBound);
  }

  private onResize(): void {
    this.game.resize(window.innerWidth, window.innerHeight);
  }

  public dispose(): void {
    cancelAnimationFrame(this.animationFrame);
    window.removeEventListener('resize', this.resizeBound);
    this.game.dispose();
  }
}

const root = document.getElementById('app');
if (!root) {
  throw new Error('Unable to locate #app');
}

/* Show a tiny loading message, then load all spritesheets + sounds. */
const loadingEl = document.createElement('div');
loadingEl.id = 'loading';
loadingEl.style.cssText =
  'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;' +
  'background:#0a0e1a;color:#ffc2dc;font-family:monospace;font-size:14px;z-index:9999;';
loadingEl.textContent = 'Loading assets...';
root.appendChild(loadingEl);

loadAssets((loaded, total) => {
  loadingEl.textContent = `Loading assets... ${Math.round((loaded / total) * 100)}%`;
})
  .then(() => {
    loadingEl.remove();
    const app = new App(root);
    window.addEventListener('beforeunload', () => {
      app.dispose();
    });

    // Fullscreen handling
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isMobile) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isStandalone = (window.navigator as any).standalone === true || window.matchMedia('(display-mode: standalone)').matches;

      if (isIOS && !isStandalone) {
        // iOS Safari: no Fullscreen API. Show "Add to Home Screen" hint
        const hint = document.createElement('div');
        hint.className = 'ios-pwa-hint';
        hint.innerHTML = `
          <button id="close-pwa-hint">✕</button>
          باش تلعب بشاشة كاملة:<br>
          اضغط على <strong>⎙</strong> (مشاركة) ← <strong>إضافة إلى الشاشة الرئيسية</strong>
        `;
        document.body.appendChild(hint);

        const closeBtn = document.getElementById('close-pwa-hint');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => hint.remove());
        }
        // Auto-dismiss after 8 seconds
        setTimeout(() => hint.remove(), 8000);
      } else if (!isIOS) {
        // Android / other: use Fullscreen API
        const fsBtn = document.createElement('button');
        fsBtn.className = 'fullscreen-btn';
        fsBtn.textContent = '⛶';
        fsBtn.setAttribute('aria-label', 'Fullscreen');
        document.body.appendChild(fsBtn);
        fsBtn.style.display = 'flex';

        const enterFullscreen = (): void => {
          const el = document.documentElement as any;
          const rfs = el.requestFullscreen
            || el.webkitRequestFullscreen
            || el.mozRequestFullScreen
            || el.msRequestFullscreen;
          if (rfs) {
            rfs.call(el).then(() => {
              try { (screen.orientation as any).lock('landscape').catch(() => {}); } catch (_e) {}
            }).catch(() => {});
          }
        };

        const updateFsButton = (): void => {
          const fsEl = (document as any).fullscreenElement
            || (document as any).webkitFullscreenElement
            || (document as any).mozFullScreenElement
            || (document as any).msFullscreenElement;
          fsBtn.style.display = fsEl ? 'none' : 'flex';
        };

        fsBtn.addEventListener('pointerdown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          enterFullscreen();
        });

        document.addEventListener('fullscreenchange', updateFsButton);
        document.addEventListener('webkitfullscreenchange', updateFsButton);
      }
      // If iOS standalone (PWA), already fullscreen — do nothing
    }
  })
  .catch((err) => {
    loadingEl.textContent = `Failed to load assets: ${err}`;
    console.error(err);
  });
